import React, { useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, Instagram, CheckCircle } from 'lucide-react';
import contactBg from '../assets/images/getintouchbg.png';

const ContactSection = () => {
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
    };

    return (
        <section id="contact" className="relative py-24 overflow-hidden min-h-[800px] flex items-center">
            {/* Background Image with Blue Tint */}
            <div className="absolute inset-0 bg-[#0a1d37]/70 z-10" />
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${contactBg})` }}
            />

            <div className="container mx-auto px-6 md:px-12 relative z-20 py-16 md:py-24">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                    {/* Contact Details (Left side) */}
                    <div className="lg:w-[45%] text-white space-y-6 md:space-y-10 w-full">
                        <div className="flex items-center gap-4 md:gap-6 group">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shrink-0 shadow-lg transition-transform group-hover:scale-110">
                                <Mail className="w-5 h-5 md:w-7 md:h-7 text-[#ff5a5f]" />
                            </div>
                            <span className="text-lg md:text-xl font-medium tracking-wide">info@mydbq.com</span>
                        </div>

                        <div className="flex items-center gap-4 md:gap-6 group">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shrink-0 shadow-lg transition-transform group-hover:scale-110">
                                <Phone className="w-5 h-5 md:w-7 md:h-7 text-[#00bfa5]" />
                            </div>
                            <span className="text-lg md:text-xl font-medium tracking-wide">+91 - 8618093629</span>
                        </div>

                        <div className="flex items-start gap-4 md:gap-6 group">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shrink-0 shadow-lg transition-transform group-hover:scale-110">
                                <MapPin className="w-5 h-5 md:w-7 md:h-7 text-[#ff5a5f]" />
                            </div>
                            <p className="text-base md:text-lg leading-relaxed font-medium pt-1">
                                No 49 , 3 rd floor , South end Main road Tata Silk Farm Basavanagudi<br />
                                Bangalore - 560004
                            </p>
                        </div>

                        <div className="flex items-start gap-4 md:gap-6 group">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shrink-0 shadow-lg transition-transform group-hover:scale-110">
                                <MapPin className="w-5 h-5 md:w-7 md:h-7 text-[#ff5a5f]" />
                            </div>
                            <p className="text-base md:text-lg leading-relaxed font-medium pt-1">
                                WJ8M+85J, 6th Main Rd, Sector 6, HSR Layout, Bengaluru,<br />
                                Karnataka, 560102
                            </p>
                        </div>

                        <div className="flex gap-4 md:gap-6 pt-4 md:pt-6">
                            <a href="https://www.instagram.com/dbqtechnologies/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                                <div className="p-2 rounded-lg bg-gradient-to-tr from-[#f09433] via-[#e6683c] to-[#bc1888] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                                    <Instagram className="w-5 h-5 md:w-6 md:h-6 text-white" />
                                </div>
                            </a>
                            <a href="https://www.linkedin.com/company/dbq-technologies-private-limited/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#0077b5] rounded-lg hover:scale-110 transition-transform">
                                <Linkedin className="w-6 h-6 md:w-7 md:h-7 text-white" />
                            </a>
                        </div>
                    </div>

                    {/* Contact Form (Right side) */}
                    <div className="lg:w-[55%] w-full">
                        <div className="bg-white p-6 md:p-14 rounded-sm shadow-2xl max-w-2xl lg:ml-auto min-h-[500px] md:min-h-[550px] flex flex-col">
                            {submitted ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6 animate-in fade-in zoom-in duration-500">
                                    <div className="flex justify-center">
                                        <CheckCircle className="w-16 h-16 md:w-24 md:h-24 text-emerald-500" />
                                    </div>
                                    <h3 className="text-[#3b82f6] text-3xl md:text-4xl font-bold">Thank You!</h3>
                                    <p className="text-gray-600 text-base md:text-lg">
                                        Your message has been successfully submitted. <br />
                                        We will get back to you shortly.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="text-[#1d4ed8] font-medium hover:underline pt-4"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h3 className="text-[#3b3ef6] text-3xl md:text-4xl font-medium mb-8 md:mb-12 tracking-tight">Get in Touch</h3>
                                    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 flex-1 flex flex-col">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <input
                                                required
                                                type="text"
                                                placeholder="First Name"
                                                className="w-full bg-[#ecf0ff] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-700/60 text-blue-900 text-sm md:text-base"
                                            />
                                            <input
                                                required
                                                type="text"
                                                placeholder="Last Name"
                                                className="w-full bg-[#ecf0ff] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-700/60 text-blue-900 text-sm md:text-base"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <input
                                                required
                                                type="email"
                                                placeholder="Email"
                                                className="w-full bg-[#ecf0ff] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-700/60 text-blue-900 text-sm md:text-base"
                                            />
                                            <input
                                                required
                                                type="tel"
                                                placeholder="Phone"
                                                className="w-full bg-[#ecf0ff] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-700/60 text-blue-900 text-sm md:text-base"
                                            />
                                        </div>
                                        <input
                                            required
                                            type="text"
                                            placeholder="Address"
                                            className="w-full bg-[#c7d2fe] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-800/60 text-blue-900 text-sm md:text-base"
                                        />
                                        <textarea
                                            required
                                            rows={4}
                                            placeholder="Type your message here"
                                            className="w-full bg-[#9daeff] px-4 md:px-6 py-4 md:py-5 rounded-md focus:outline-none placeholder:text-blue-900/60 text-blue-950 text-sm md:text-base resize-none flex-1"
                                        />
                                        <div className="flex justify-center pt-6 md:pt-8">
                                            <button type="submit" className="bg-[#1d42f8] hover:bg-blue-700 text-white font-medium py-3 md:py-4 px-12 md:px-16 rounded-full transition-all text-sm md:text-base shadow-lg hover:shadow-xl active:scale-95">
                                                Submit
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
export default ContactSection;
